import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './demos.module.css';

/** Clause slots. Text comes from the locale files, so the demo speaks he/ar. */
const CLAUSE_KEYS = ['c1', 'c2', 'c3', 'c4'];

/**
 * Clause assembly, then a signature that draws itself.
 *
 * `animate={false}` renders the finished state immediately — used by tests and
 * by anyone with prefers-reduced-motion, which is honoured here in JS rather
 * than only in CSS because the reveal is staged in state, not in a keyframe.
 */
const AgreementDemo = ({ color = '#10b981', animate = true }) => {
  const { t } = useTranslation();
  const reduced = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const staged = animate && !reduced;

  const [shown, setShown] = useState(staged ? 0 : CLAUSE_KEYS.length);
  const [signed, setSigned] = useState(!staged);

  useEffect(() => {
    if (!staged) return undefined;
    const timers = CLAUSE_KEYS.map((_, i) =>
      setTimeout(() => setShown(i + 1), 260 * (i + 1)));
    const sign = setTimeout(() => setSigned(true), 260 * (CLAUSE_KEYS.length + 1));
    return () => { timers.forEach(clearTimeout); clearTimeout(sign); };
  }, [staged]);

  return (
    <div className={styles.demoFrame} data-demo="agreements">
      <div className={styles.agreementStage} data-testid="agreement-stage">
        <div className={styles.agreementDoc}>
          {CLAUSE_KEYS.slice(0, shown).map((c, i) => (
            <div
              key={c}
              className={styles.agreementClause}
              data-testid="agreement-clause"
              style={{ borderInlineStartColor: color, animationDelay: `${i * 40}ms` }}
            >
              <strong>{t(`demos.agreement.${c}t`)}</strong>
              <p>{t(`demos.agreement.${c}b`)}</p>
            </div>
          ))}

          {signed && (
            <div className={styles.agreementSign}>
              <svg viewBox="0 0 220 60" className={styles.signatureSvg} aria-hidden="true">
                <path
                  className={styles.signaturePath}
                  d="M8,44 C26,10 38,52 54,30 C70,8 78,50 96,34 C112,20 124,46 142,28 C158,12 174,40 212,20"
                  fill="none"
                  stroke={color}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              <div className={styles.agreementStamp} style={{ background: `${color}18`, color }}>
                {t('demos.agreement.signed')}
              </div>
            </div>
          )}
        </div>

        <p className={styles.agreementScope} data-testid="agreement-scope">
          <Trans i18nKey="demos.agreement.scope" components={{ 1: <strong /> }} />
        </p>
      </div>
    </div>
  );
};

export default AgreementDemo;
